import {ChangeDetectionStrategy, Component, computed, effect, input, resource} from '@angular/core';
import {FormBuilder, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {IncomeOptionSelectComponent} from "../income-option-select/income-option-select.component";
import {getLowerIncomeBound, getUpperIncomeBound, IncomeOption} from "../../model/user";
import {ActivatedRoute, Router} from "@angular/router";
import {delay, first, firstValueFrom, map, Observable, switchMap} from "rxjs";
import typia from "typia";
import {ApartmentService} from "../../services/apartment.service";
import {AsyncPipe} from "@angular/common";
import {Divider} from "primeng/divider";
import {LotteriesCarouselComponent} from "../../lotteries-carousel/lotteries-carousel.component";
import {ApartmentDto} from "homey-housing-lottery-app-dto";
import {toSignal} from "@angular/core/rxjs-interop";
import {enforceType} from "../../utils";
import {RelevantApartmentsInput} from "../../pipes/apartment-relevant-advertisements.pipe";
import {HouseholdSizeInputComponent} from "../household-size-input/household-size-input.component";
import {Skeleton} from "primeng/skeleton";

@Component({
  selector: 'homey-housing-opportunities-search',
  imports: [
    FormsModule,
    IncomeOptionSelectComponent,
    ReactiveFormsModule,
    AsyncPipe,
    Divider,
    LotteriesCarouselComponent,
    HouseholdSizeInputComponent,
    Skeleton,
  ],
  templateUrl: './housing-opportunities-search.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HousingOpportunitiesSearchComponent {
  public readonly route = input.required<string[]>();
  public readonly replace = input.required<boolean>();
  public readonly excludeLotteryId = input<string>();
  public readonly dividerBackground = input<string>('{surface.100}');

  protected readonly params$: Observable<{
    size: number,
    income: IncomeOption
  }> = this.activatedRoute.queryParamMap.pipe(
    map(params => {
        const income = params.get('income');
        return ({
          size: Number(params.get('size') ?? 2),
          income: typia.is<IncomeOption>(income) ? income : '50000-100000'
        });
      }
    ));


  protected readonly initialParams$: Observable<{
    size: number,
    income: IncomeOption
  }> = this.params$.pipe(
    first(),
  )

  protected readonly dividerDt = computed(() => {
    return {
      root: {
        content: {
          background: this.dividerBackground(),
        }
      },
    }
  });

  protected initialParams = toSignal(this.initialParams$, {initialValue: {size: 0, income: '20000-50000'}});

  protected readonly apartments$ = this.params$.pipe(
    switchMap(params => this.apartmentService.getRelevantApartmentsForIncomeAndSize(params.income, params.size))
  );

  protected params = toSignal(this.params$, {initialValue: {size: 0, income: '20000-50000'}});

  protected readonly apartmentSearchResult = resource<ApartmentDto[], {
    income: IncomeOption;
    size: number;
    excludeLotteryId?: string;
  }>({
    params: () => ({
      income: this.params().income,
      size: this.params().size,
      excludeLotteryId: this.excludeLotteryId()
    }),
    loader: async ({params}) => {
      return await firstValueFrom(
        this.apartmentService.getRelevantApartmentsForIncomeAndSize(params.income, params.size).pipe(
          map(apartments => apartments.filter(apartment => apartment.id !== params.excludeLotteryId)),
          delay(300)
        )
      );
    },
    defaultValue: [] as ApartmentDto[]
  });

  protected lowerIncomeBound = computed(() => getLowerIncomeBound(this.params().income));
  protected upperIncomeBound = computed(() => getUpperIncomeBound(this.params().income));
  protected relevantApartmentsInput = computed(
    () => enforceType<RelevantApartmentsInput>({
      type: 'specified',
      householdSize: this.params().size,
      lowerHouseholdIncome: this.lowerIncomeBound(),
      upperHouseholdIncome: this.upperIncomeBound(),
      receivesRentalAssistance: false,
      eligibleForSenior: true
    })
  );
  onSearch = () => {
    Object.values(this.searchForm().controls).forEach(control => control.markAsDirty());

    if (this.searchForm().invalid) {
      return;
    }
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParamsHandling: 'merge',
      replaceUrl: this.replace(),
      queryParams: {size: this.searchForm().value.householdSize, income: this.searchForm().value.householdIncome}
    });
  }

  protected readonly searchForm = computed(
    () => this.fb.group({
      householdSize: this.fb.nonNullable.control<number | undefined>(this.initialParams().size, Validators.required),
      householdIncome: this.fb.nonNullable.control<IncomeOption | undefined>(this.initialParams().income, Validators.required)
    })
  );

  constructor(private readonly router: Router,
              private readonly activatedRoute: ActivatedRoute,
              private readonly apartmentService: ApartmentService,
              private readonly fb: FormBuilder) {
    effect(() => this.searchForm().valueChanges.subscribe(
      this.onSearch
    )); //TODO Memory leak?
  }
}
